/**
 * TalentLens AI - Enhanced Search Page
 * Advanced search with filters, instant results, and smart features
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import api from '../lib/api';
import { useSearchStore, POPULAR_SKILLS, QUICK_FILTERS } from '../store/search';
import { useAuthStore, canExport } from '../store/auth';

// Debounce utility
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

interface SearchFilters {
  skills: string[];
  minYearsExp: number | null;
  maxYearsExp: number | null;
  status: string[];
  dateRange: 'all' | 'today' | 'week' | 'month' | 'custom';
  customDateStart?: string;
  customDateEnd?: string;
  educationLevels: string[];
  certifications: string[];
  jobTitles: string[];
  companies: string[];
}

const initialFilters: SearchFilters = {
  skills: [],
  minYearsExp: null,
  maxYearsExp: null,
  status: [],
  dateRange: 'all',
  educationLevels: [],
  certifications: [],
  jobTitles: [],
  companies: [],
};

export default function SearchPage() {
  const { user } = useAuthStore();
  const {
    recentSearches,
    addRecentSearch,
    removeRecentSearch,
    clearRecentSearches,
    viewMode,
    setViewMode,
    sortBy,
    setSortBy,
    selectedResults,
    toggleSelected,
    clearSelected,
    preferences,
  } = useSearchStore();

  // Search state
  const [query, setQuery] = useState('');
  const [semantic, setSemantic] = useState(preferences.defaultSemantic);
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Debounced query for instant search
  const debouncedQuery = useDebounce(query, 400);

  // Auto-focus search input
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Check for incoming filter from CV detail page
  useEffect(() => {
    const storedFilter = sessionStorage.getItem('searchFilter');
    if (storedFilter) {
      try {
        const { type, value } = JSON.parse(storedFilter);
        sessionStorage.removeItem('searchFilter');

        // Apply the filter based on type
        if (type === 'skills') {
          setFilters(prev => ({ ...prev, skills: [value] }));
          setQuery(value);
        } else if (type === 'companies') {
          setFilters(prev => ({ ...prev, companies: [value] }));
          setQuery(`company:${value}`);
        } else if (type === 'jobTitles') {
          setFilters(prev => ({ ...prev, jobTitles: [value] }));
          setQuery(value);
        } else if (type === 'certifications') {
          setFilters(prev => ({ ...prev, certifications: [value] }));
          setQuery(value);
        }

        setShowFilters(true);
      } catch (e) {
        console.error('Failed to parse search filter', e);
      }
    }
  }, []);

  // Keyboard shortcut: / to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && document.activeElement !== searchInputRef.current) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        searchInputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Fetch available filters
  const { data: availableFilters } = useQuery({
    queryKey: ['search-filters'],
    queryFn: () => api.getSearchFilters(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch suggestions — use debouncedQuery to avoid firing on every keystroke
  const { data: suggestions } = useQuery({
    queryKey: ['suggestions', debouncedQuery],
    queryFn: ({ signal }) => api.getSearchSuggestions(debouncedQuery, signal),
    enabled: debouncedQuery.length >= 2 && !debouncedQuery.endsWith(' '),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  // Main search query - triggers on debounced query change
  // sortBy removed from queryKey — sorting is client-side only
  const { data: results, isLoading, isFetching } = useQuery({
    queryKey: ['search', debouncedQuery, semantic, filters],
    queryFn: async ({ signal }) => {
      const response = await api.search(debouncedQuery, {
        ...filters,
        skills: filters.skills.length > 0 ? filters.skills : undefined,
        years_experience: filters.minYearsExp || filters.maxYearsExp ? {
          min: filters.minYearsExp || undefined,
          max: filters.maxYearsExp || undefined,
        } : undefined,
        status: filters.status.length > 0 ? filters.status : undefined,
        date_range: filters.dateRange !== 'all' && filters.dateRange !== 'custom' ? {
          start: getDateFromRange(filters.dateRange),
        } : filters.dateRange === 'custom' && filters.customDateStart ? {
          start: new Date(filters.customDateStart),
          end: filters.customDateEnd ? new Date(filters.customDateEnd) : undefined,
        } : undefined,
        education_levels: filters.educationLevels.length > 0 ? filters.educationLevels : undefined,
        certifications: filters.certifications.length > 0 ? filters.certifications : undefined,
        job_titles: filters.jobTitles.length > 0 ? filters.jobTitles : undefined,
        companies: filters.companies.length > 0 ? filters.companies : undefined,
      }, { semantic, limit: 50 }, signal);
      return response;
    },
    enabled: debouncedQuery.length > 0,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  // Add to recent searches when search is executed
  useEffect(() => {
    if (debouncedQuery && results?.data?.results?.length > 0) {
      addRecentSearch(debouncedQuery, filters);
    }
  }, [debouncedQuery, results]);

  const handleSearch = useCallback((searchQuery: string) => {
    setQuery(searchQuery);
    setShowSuggestions(false);
    searchInputRef.current?.focus();
  }, []);

  const handleSuggestionSelect = useCallback((skill: string) => {
    setQuery(skill);
    setShowSuggestions(false);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const suggestionsList = suggestions?.data?.suggestions || [];

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev =>
        Math.min(prev + 1, suggestionsList.length - 1)
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSuggestionSelect(suggestionsList[selectedSuggestionIndex].skill);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  }, [suggestions, selectedSuggestionIndex, handleSuggestionSelect]);

  const addSkillFilter = useCallback((skill: string) => {
    if (skill && !filters.skills.includes(skill)) {
      setFilters(prev => ({ ...prev, skills: [...prev.skills, skill] }));
    }
  }, [filters.skills]);

  const removeSkillFilter = useCallback((skill: string) => {
    setFilters(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill),
    }));
  }, []);

  const toggleStatusFilter = useCallback((status: string) => {
    setFilters(prev => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter(s => s !== status)
        : [...prev.status, status],
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(initialFilters);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.skills.length > 0 ||
      filters.minYearsExp !== null ||
      filters.maxYearsExp !== null ||
      filters.status.length > 0 ||
      filters.dateRange !== 'all' ||
      filters.educationLevels.length > 0 ||
      filters.certifications.length > 0 ||
      filters.jobTitles.length > 0 ||
      filters.companies.length > 0
    );
  }, [filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.skills.length > 0) count += filters.skills.length;
    if (filters.minYearsExp !== null || filters.maxYearsExp !== null) count += 1;
    if (filters.status.length > 0) count += filters.status.length;
    if (filters.dateRange !== 'all') count += 1;
    if (filters.educationLevels.length > 0) count += filters.educationLevels.length;
    if (filters.certifications.length > 0) count += filters.certifications.length;
    if (filters.jobTitles.length > 0) count += filters.jobTitles.length;
    if (filters.companies.length > 0) count += filters.companies.length;
    return count;
  }, [filters]);

  const searchResults = results?.data?.results || [];
  const availableSkills = availableFilters?.data?.skills || [];
  const availableCertifications = availableFilters?.data?.certifications || [];
  const availableJobTitles = availableFilters?.data?.jobTitles || [];
  const availableCompanies = availableFilters?.data?.companies || [];
  const availableEducationLevels = availableFilters?.data?.educationLevels || [];
  const queryExpanded = results?.data?.query?.expanded;

  // Sort results client-side based on sortBy
  const sortedResults = useMemo(() => {
    if (sortBy === 'relevance') return [...searchResults].sort((a, b) => b.score - a.score);

    return [...searchResults].sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      if (sortBy === 'experience') {
        const aExp = Math.max(...(a.skills?.map((s: any) => s.years) || [0]));
        const bExp = Math.max(...(b.skills?.map((s: any) => s.years) || [0]));
        return bExp - aExp;
      }
      return 0;
    });
  }, [searchResults, sortBy]);

  const handleExport = async () => {
    const cvIds = selectedResults.size > 0
      ? Array.from(selectedResults)
      : searchResults.map((r: any) => r.id);

    try {
      const csv = await api.exportCVsCSV(cvIds);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `search-results-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  return (
    <div className="min-h-[calc(100vh-8rem)]">
      {/* Search Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6">
        {/* Search Input */}
        <div className="relative">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setShowSuggestions(true);
                  setSelectedSuggestionIndex(-1);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                onKeyDown={handleKeyDown}
                placeholder="Search candidates... (e.g., 'Senior Python developer with 5+ years experience')"
                className="w-full pl-12 pr-4 py-3 text-lg border dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white dark:bg-gray-700 dark:text-white"
                aria-label="Search candidates"
                aria-describedby="search-help"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full"
                  aria-label="Clear search"
                >
                  <CloseIcon className="w-4 h-4 text-gray-400" />
                </button>
              )}

              {/* Suggestions Dropdown */}
              {showSuggestions && suggestions?.data?.suggestions && suggestions.data.suggestions.length > 0 && query.length >= 2 && (
                <div
                  ref={suggestionsRef}
                  className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-xl shadow-lg z-20 max-h-64 overflow-y-auto"
                  role="listbox"
                >
                  {suggestions.data.suggestions.map((s: any, i: number) => (
                    <button
                      key={i}
                      onClick={() => handleSuggestionSelect(s.skill)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center justify-between ${
                        i === selectedSuggestionIndex ? 'bg-primary-50 dark:bg-primary-900/30' : ''
                      } ${i === 0 ? 'rounded-t-xl' : ''} ${
                        i === suggestions.data.suggestions.length - 1 ? 'rounded-b-xl' : ''
                      }`}
                      role="option"
                      aria-selected={i === selectedSuggestionIndex}
                    >
                      <span className="font-medium dark:text-white">{s.skill}</span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {s.count} candidates
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-3 border rounded-xl transition-colors ${
                showFilters || hasActiveFilters
                  ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-300 dark:border-primary-600 text-primary-700 dark:text-primary-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              aria-expanded={showFilters}
              aria-controls="filter-panel"
            >
              <FilterIcon className="w-5 h-5" />
              <span className="hidden sm:inline">Filters</span>
              {activeFilterCount > 0 && (
                <span className="px-2 py-0.5 bg-primary-600 text-white text-xs rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>

            <label className="hidden md:flex items-center gap-2 px-4 py-3 border dark:border-gray-600 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
              <input
                type="checkbox"
                checked={semantic}
                onChange={(e) => setSemantic(e.target.checked)}
                className="rounded text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                AI Search
              </span>
            </label>
          </div>

          {/* Search Tips */}
          <p id="search-help" className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-600 rounded text-xs">/</kbd> to focus
            {' · '}
            Use natural language or boolean operators (AND, OR, NOT)
          </p>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div id="filter-panel" className="mt-6 pt-6 border-t dark:border-gray-600">
            {/* Row 1: Primary filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {/* Skills Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Skills
                </label>
                <SkillsFilter
                  availableSkills={availableSkills}
                  selectedSkills={filters.skills}
                  onAdd={addSkillFilter}
                  onRemove={removeSkillFilter}
                />
              </div>

              {/* Experience Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Experience (Years)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="50"
                    placeholder="Min"
                    value={filters.minYearsExp ?? ''}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      minYearsExp: e.target.value ? Number(e.target.value) : null,
                    }))}
                    className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-gray-700 dark:text-white"
                  />
                  <span className="text-gray-500">-</span>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    placeholder="Max"
                    value={filters.maxYearsExp ?? ''}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      maxYearsExp: e.target.value ? Number(e.target.value) : null,
                    }))}
                    className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              {/* Education Level Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Education Level
                </label>
                <div className="flex flex-wrap gap-2">
                  {availableEducationLevels.map((level: any) => (
                    <button
                      key={level.id}
                      onClick={() => setFilters(prev => ({
                        ...prev,
                        educationLevels: prev.educationLevels.includes(level.id)
                          ? prev.educationLevels.filter(l => l !== level.id)
                          : [...prev.educationLevels, level.id],
                      }))}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        filters.educationLevels.includes(level.id)
                          ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-600'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                      title={`${level.count} candidates`}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Certifications Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Certifications
                </label>
                <MultiSelectFilter
                  availableItems={availableCertifications.map((c: any) => ({ value: c.name, label: c.name, count: c.count }))}
                  selectedItems={filters.certifications}
                  onChange={(items) => setFilters(prev => ({ ...prev, certifications: items }))}
                  placeholder="Search certifications..."
                />
              </div>
            </div>

            {/* Row 2: Secondary filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Job Titles Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Job Titles
                </label>
                <MultiSelectFilter
                  availableItems={availableJobTitles.map((j: any) => ({ value: j.title, label: j.title, count: j.count }))}
                  selectedItems={filters.jobTitles}
                  onChange={(items) => setFilters(prev => ({ ...prev, jobTitles: items }))}
                  placeholder="Search job titles..."
                />
              </div>

              {/* Companies Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Companies
                </label>
                <MultiSelectFilter
                  availableItems={availableCompanies.map((c: any) => ({ value: c.company, label: c.company, count: c.count }))}
                  selectedItems={filters.companies}
                  onChange={(items) => setFilters(prev => ({ ...prev, companies: items }))}
                  placeholder="Search companies..."
                />
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <div className="flex flex-wrap gap-2">
                  {['processed', 'pending', 'failed'].map(status => (
                    <button
                      key={status}
                      onClick={() => toggleStatusFilter(status)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        filters.status.includes(status)
                          ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-600'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Range Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Added
                </label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    dateRange: e.target.value as any,
                  }))}
                  className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-gray-700 dark:text-white"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="custom">Custom Range</option>
                </select>

                {filters.dateRange === 'custom' && (
                  <div className="flex gap-2 mt-2">
                    <input
                      type="date"
                      value={filters.customDateStart || ''}
                      onChange={(e) => setFilters(prev => ({
                        ...prev,
                        customDateStart: e.target.value,
                      }))}
                      className="flex-1 px-2 py-1.5 text-sm border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
                    />
                    <input
                      type="date"
                      value={filters.customDateEnd || ''}
                      onChange={(e) => setFilters(prev => ({
                        ...prev,
                        customDateEnd: e.target.value,
                      }))}
                      className="flex-1 px-2 py-1.5 text-sm border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Active Filters & Clear */}
            {hasActiveFilters && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t dark:border-gray-600">
                <div className="flex flex-wrap gap-2">
                  {filters.skills.map(skill => (
                    <span
                      key={skill}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full text-sm"
                    >
                      {skill}
                      <button
                        onClick={() => removeSkillFilter(skill)}
                        className="hover:text-primary-900 dark:hover:text-primary-100"
                      >
                        <CloseIcon className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                  {(filters.minYearsExp || filters.maxYearsExp) && (
                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-full text-sm">
                      {filters.minYearsExp || 0}–{filters.maxYearsExp || '50'}+ years
                    </span>
                  )}
                  {filters.educationLevels.map(level => (
                    <span
                      key={level}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 rounded-full text-sm"
                    >
                      {availableEducationLevels.find((l: any) => l.id === level)?.label || level}
                      <button
                        onClick={() => setFilters(prev => ({
                          ...prev,
                          educationLevels: prev.educationLevels.filter(l => l !== level),
                        }))}
                        className="hover:text-indigo-900 dark:hover:text-indigo-100"
                      >
                        <CloseIcon className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                  {filters.certifications.map(cert => (
                    <span
                      key={cert}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded-full text-sm"
                    >
                      <CertificateIcon className="w-3.5 h-3.5" />
                      {cert}
                      <button
                        onClick={() => setFilters(prev => ({
                          ...prev,
                          certifications: prev.certifications.filter(c => c !== cert),
                        }))}
                        className="hover:text-amber-900 dark:hover:text-amber-100"
                      >
                        <CloseIcon className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                  {filters.jobTitles.map(title => (
                    <span
                      key={title}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300 rounded-full text-sm"
                    >
                      {title}
                      <button
                        onClick={() => setFilters(prev => ({
                          ...prev,
                          jobTitles: prev.jobTitles.filter(t => t !== title),
                        }))}
                        className="hover:text-cyan-900 dark:hover:text-cyan-100"
                      >
                        <CloseIcon className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                  {filters.companies.map(company => (
                    <span
                      key={company}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 rounded-full text-sm"
                    >
                      <BuildingIcon className="w-3.5 h-3.5" />
                      {company}
                      <button
                        onClick={() => setFilters(prev => ({
                          ...prev,
                          companies: prev.companies.filter(c => c !== company),
                        }))}
                        className="hover:text-teal-900 dark:hover:text-teal-100"
                      >
                        <CloseIcon className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                  {filters.status.map(status => (
                    <span
                      key={status}
                      className="px-3 py-1 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded-full text-sm"
                    >
                      {status}
                    </span>
                  ))}
                  {filters.dateRange !== 'all' && (
                    <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded-full text-sm">
                      {filters.dateRange === 'custom' ? 'Custom dates' : `This ${filters.dateRange}`}
                    </span>
                  )}
                </div>
                <button
                  onClick={clearFilters}
                  className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                >
                  Clear All
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main Content Area */}
      {!debouncedQuery ? (
        /* Zero State */
        <ZeroState
          recentSearches={recentSearches}
          onSearch={handleSearch}
          onRemoveRecent={removeRecentSearch}
          onClearRecent={clearRecentSearches}
        />
      ) : (
        /* Results Area */
        <div>
          {/* Results Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {isFetching ? 'Searching...' : `${sortedResults.length} Results`}
              </h2>
              {queryExpanded && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  <SparklesIcon className="w-4 h-4 inline mr-1" />
                  AI understood: <span className="font-medium">{queryExpanded}</span>
                </p>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Bulk Actions */}
              {selectedResults.size > 0 && (
                <div className="flex items-center gap-2 mr-4">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedResults.size} selected
                  </span>
                  <button
                    onClick={clearSelected}
                    className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    Clear
                  </button>
                </div>
              )}

              {/* Export */}
              {canExport(user) && sortedResults.length > 0 && (
                <button
                  onClick={handleExport}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  <ExportIcon className="w-4 h-4" />
                  Export
                </button>
              )}

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-3 py-1.5 text-sm border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
              >
                <option value="relevance">Sort: Relevance</option>
                <option value="date">Sort: Most Recent</option>
                <option value="experience">Sort: Experience</option>
              </select>

              {/* View Mode */}
              <div className="flex border dark:border-gray-600 rounded-lg overflow-hidden">
                {(['compact', 'standard', 'detailed'] as const).map(mode => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={`px-3 py-1.5 text-sm ${
                      viewMode === mode
                        ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300'
                        : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                    title={`${mode.charAt(0).toUpperCase() + mode.slice(1)} view`}
                  >
                    {mode === 'compact' && <ListIcon className="w-4 h-4" />}
                    {mode === 'standard' && <GridIcon className="w-4 h-4" />}
                    {mode === 'detailed' && <ExpandIcon className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 animate-pulse">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full" />
                    <div className="flex-1 space-y-3">
                      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
                      <div className="flex gap-2">
                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-16" />
                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-20" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Results */}
          {!isLoading && sortedResults.length === 0 && (
            <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
              <SearchIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No candidates found
              </h3>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-6">
                Try different keywords or adjust your filters
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setQuery('')}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Clear Search
                </button>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-primary-700 dark:text-primary-300 bg-primary-100 dark:bg-primary-900/50 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-900"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Results List */}
          {!isLoading && sortedResults.length > 0 && (
            <div className={`space-y-${viewMode === 'compact' ? '2' : '4'}`}>
              {sortedResults.map((result: any) => (
                <ResultCard
                  key={result.id}
                  result={result}
                  viewMode={viewMode}
                  query={query}
                  filterSkills={filters.skills}
                  isSelected={selectedResults.has(result.id)}
                  onToggleSelect={() => toggleSelected(result.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Skills Filter Component with search
function SkillsFilter({
  availableSkills,
  selectedSkills,
  onAdd,
  onRemove,
}: {
  availableSkills: any[];
  selectedSkills: string[];
  onAdd: (skill: string) => void;
  onRemove: (skill: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filteredSkills = availableSkills
    .filter((s: any) =>
      !selectedSkills.includes(s.skill) &&
      s.skill.toLowerCase().includes(search.toLowerCase())
    )
    .slice(0, 10);

  return (
    <div className="relative">
      <div
        className="min-h-[42px] p-2 border dark:border-gray-600 rounded-lg cursor-text bg-white dark:bg-gray-700"
        onClick={() => setIsOpen(true)}
      >
        <div className="flex flex-wrap gap-1">
          {selectedSkills.map(skill => (
            <span
              key={skill}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded text-sm"
            >
              {skill}
              <button onClick={(e) => { e.stopPropagation(); onRemove(skill); }}>
                <CloseIcon className="w-3 h-3" />
              </button>
            </span>
          ))}
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setTimeout(() => setIsOpen(false), 200)}
            placeholder={selectedSkills.length === 0 ? 'Type to search skills...' : ''}
            className="flex-1 min-w-[100px] outline-none bg-transparent dark:text-white text-sm"
          />
        </div>
      </div>

      {isOpen && filteredSkills.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
          {filteredSkills.map((s: any) => (
            <button
              key={s.skill}
              onClick={() => {
                onAdd(s.skill);
                setSearch('');
              }}
              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 dark:text-white flex justify-between"
            >
              <span>{s.skill}</span>
              <span className="text-gray-400">{s.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Multi-select filter component for certifications, job titles, companies
function MultiSelectFilter({
  availableItems,
  selectedItems,
  onChange,
  placeholder,
}: {
  availableItems: Array<{ value: string; label: string; count: number }>;
  selectedItems: string[];
  onChange: (items: string[]) => void;
  placeholder: string;
}) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filteredItems = availableItems
    .filter(
      (item) =>
        !selectedItems.includes(item.value) &&
        item.label.toLowerCase().includes(search.toLowerCase())
    )
    .slice(0, 10);

  const addItem = (value: string) => {
    onChange([...selectedItems, value]);
    setSearch('');
  };

  const removeItem = (value: string) => {
    onChange(selectedItems.filter((i) => i !== value));
  };

  return (
    <div className="relative">
      <div
        className="min-h-[42px] p-2 border dark:border-gray-600 rounded-lg cursor-text bg-white dark:bg-gray-700"
        onClick={() => setIsOpen(true)}
      >
        <div className="flex flex-wrap gap-1">
          {selectedItems.map((item) => {
            const itemData = availableItems.find((i) => i.value === item);
            return (
              <span
                key={item}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded text-sm"
              >
                {itemData?.label || item}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeItem(item);
                  }}
                >
                  <CloseIcon className="w-3 h-3" />
                </button>
              </span>
            );
          })}
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setTimeout(() => setIsOpen(false), 200)}
            placeholder={selectedItems.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[100px] outline-none bg-transparent dark:text-white text-sm"
          />
        </div>
      </div>

      {isOpen && filteredItems.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
          {filteredItems.map((item) => (
            <button
              key={item.value}
              onClick={() => addItem(item.value)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 dark:text-white flex justify-between"
            >
              <span className="truncate">{item.label}</span>
              <span className="text-gray-400 ml-2 flex-shrink-0">{item.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Zero State Component
function ZeroState({
  recentSearches,
  onSearch,
  onRemoveRecent,
  onClearRecent,
}: {
  recentSearches: any[];
  onSearch: (query: string) => void;
  onRemoveRecent: (query: string) => void;
  onClearRecent: () => void;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-8">
      <div className="max-w-2xl mx-auto text-center">
        <SearchIcon className="w-16 h-16 text-primary-200 dark:text-primary-800 mx-auto mb-6" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Search Your Talent Pool
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          Find the perfect candidates using natural language, keywords, or filters
        </p>

        {/* Recent Searches */}
        {recentSearches.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Recent Searches</h3>
              <button
                onClick={onClearRecent}
                className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Clear all
              </button>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {recentSearches.slice(0, 5).map((search, i) => (
                <button
                  key={i}
                  onClick={() => onSearch(search.query)}
                  className="group flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  <ClockIcon className="w-4 h-4 text-gray-400" />
                  {search.query}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveRecent(search.query);
                    }}
                    className="opacity-0 group-hover:opacity-100 hover:text-red-500"
                  >
                    <CloseIcon className="w-3.5 h-3.5" />
                  </button>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Popular Skills */}
        <div className="mb-8">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Popular Skills</h3>
          <div className="flex flex-wrap justify-center gap-2">
            {POPULAR_SKILLS.map(skill => (
              <button
                key={skill}
                onClick={() => onSearch(skill)}
                className="px-4 py-2 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/50"
              >
                {skill}
              </button>
            ))}
          </div>
        </div>

        {/* Quick Filters */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Quick Filters</h3>
          <div className="flex flex-wrap justify-center gap-2">
            {QUICK_FILTERS.map((filter, i) => (
              <button
                key={i}
                onClick={() => {
                  if (filter.filters.minYearsExp) {
                    onSearch(`${filter.filters.minYearsExp}+ years experience`);
                  } else if (filter.filters.roleLevel) {
                    onSearch(`${filter.filters.roleLevel} developer`);
                  } else {
                    onSearch('');
                  }
                }}
                className="px-4 py-2 border dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Result Card Component
function ResultCard({
  result,
  viewMode,
  query,
  filterSkills,
  isSelected,
  onToggleSelect,
}: {
  result: any;
  viewMode: 'compact' | 'standard' | 'detailed';
  query: string;
  filterSkills: string[];
  isSelected: boolean;
  onToggleSelect: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  // Check if a skill matches the search query, filters, or matched terms from backend
  const isSkillMatch = (skill: string) => {
    const skillLower = skill.toLowerCase();

    // Check against explicit skill filters
    if (filterSkills.some(f => f.toLowerCase() === skillLower)) {
      return true;
    }

    // Check against the search query
    if (query.toLowerCase().includes(skillLower)) {
      return true;
    }

    // Check against matched terms from backend score breakdown
    if (result.matchedTerms?.some((term: string) =>
      skillLower.includes(term.toLowerCase()) || term.toLowerCase().includes(skillLower)
    )) {
      return true;
    }

    // Check against the skills breakdown from the backend
    if (result.scoreBreakdown?.skills?.matches?.some((match: any) =>
      match.skill.toLowerCase() === skillLower
    )) {
      return true;
    }

    return false;
  };

  if (viewMode === 'compact') {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 flex items-center gap-4 ${
        isSelected ? 'ring-2 ring-primary-500' : ''
      }`}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="rounded text-primary-600 focus:ring-primary-500"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <Link
              to="/cvs/$cvId"
              params={{ cvId: result.id }}
              className="font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 truncate"
            >
              {result.name || 'Unknown'}
            </Link>
            <ScoreBadge
              score={result.score}
              breakdown={result.scoreBreakdown}
              vectorScore={result.vectorScore}
              textScore={result.textScore}
              matchedTerms={result.matchedTerms}
            />
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2">
          {result.skills?.slice(0, 3).map((s: any, i: number) => (
            <span
              key={i}
              className={`px-2 py-0.5 rounded text-xs ${
                isSkillMatch(s.skill)
                  ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}
            >
              {s.skill}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {result.email && (
            <a
              href={`mailto:${result.email}`}
              className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
              title="Contact"
            >
              <MailIcon className="w-5 h-5" />
            </a>
          )}
          <Link
            to="/cvs/$cvId"
            params={{ cvId: result.id }}
            className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
            title="View CV"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm ${
      isSelected ? 'ring-2 ring-primary-500' : ''
    }`}>
      <div className="p-6">
        <div className="flex items-start gap-4">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="mt-1 rounded text-primary-600 focus:ring-primary-500"
          />

          {/* Avatar */}
          <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center text-primary-700 dark:text-primary-300 font-semibold flex-shrink-0">
            {(result.name || '?').split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase()}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <Link
                    to="/cvs/$cvId"
                    params={{ cvId: result.id }}
                    className="text-lg font-semibold text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400"
                  >
                    {result.name || 'Unknown'}
                  </Link>
                  <ScoreBadge
                    score={result.score}
                    breakdown={result.scoreBreakdown}
                    vectorScore={result.vectorScore}
                    textScore={result.textScore}
                    matchedTerms={result.matchedTerms}
                  />
                </div>

                {result.email && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{result.email}</p>
                )}

                {/* Skills */}
                {result.skills && result.skills.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {result.skills.slice(0, viewMode === 'detailed' ? 12 : 8).map((skill: any, i: number) => (
                      <span
                        key={i}
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          isSkillMatch(skill.skill)
                            ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {skill.skill}
                        {skill.years > 0 && (
                          <span className="ml-1 opacity-70">({skill.years}y)</span>
                        )}
                      </span>
                    ))}
                    {result.skills.length > (viewMode === 'detailed' ? 12 : 8) && (
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded text-xs">
                        +{result.skills.length - (viewMode === 'detailed' ? 12 : 8)} more
                      </span>
                    )}
                  </div>
                )}

                {/* Experience */}
                {result.experience && result.experience.length > 0 && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <BriefcaseIcon className="w-4 h-4 inline mr-1" />
                    {result.experience[0].title} at {result.experience[0].company}
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {result.email && (
                  <a
                    href={`mailto:${result.email}`}
                    className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    title="Contact Candidate"
                  >
                    <MailIcon className="w-5 h-5" />
                  </a>
                )}
                {viewMode === 'detailed' && (
                  <button
                    onClick={() => setExpanded(!expanded)}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    {expanded ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
                  </button>
                )}
                <Link
                  to="/cvs/$cvId"
                  params={{ cvId: result.id }}
                  className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  title="View CV"
                >
                  <ViewIcon className="w-5 h-5" />
                </Link>
              </div>
            </div>

            {/* Added date */}
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
              Added {new Date(result.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Expanded Details */}
        {viewMode === 'detailed' && expanded && (
          <div className="mt-4 pt-4 border-t dark:border-gray-700 ml-16">
            {result.experience && result.experience.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Experience</h4>
                <div className="space-y-2">
                  {result.experience.slice(0, 3).map((exp: any, i: number) => (
                    <div key={i} className="text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-300">{exp.title}</span>
                      <span className="text-gray-500 dark:text-gray-400"> at {exp.company}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Link
                to="/cvs/$cvId"
                params={{ cvId: result.id }}
                className="flex-1 text-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                View Full Profile
              </Link>
              <button className="px-4 py-2 border dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                Compare
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Score Breakdown interface
interface ScoreBreakdownData {
  summary?: string;
  certifications?: {
    score: number;
    maxScore: number;
    matches: Array<{ name: string; term: string; isRecent?: boolean }>;
  };
  skills?: {
    score: number;
    maxScore: number;
    matches: Array<{ skill: string; term: string; years: number }>;
  };
  experienceTitles?: {
    score: number;
    maxScore: number;
    matches: Array<{ title: string; company: string; term: string; isRecent?: boolean }>;
  };
  experienceDescriptions?: {
    score: number;
    maxScore: number;
    matchCount: number;
  };
  education?: {
    score: number;
    maxScore: number;
    matches: Array<{ degree: string; institution: string; term: string }>;
  };
}

// Score Badge Component with Tooltip
function ScoreBadge({
  score,
  breakdown,
  vectorScore,
  textScore,
  matchedTerms,
}: {
  score: number;
  breakdown?: ScoreBreakdownData;
  vectorScore?: number;
  textScore?: number;
  matchedTerms?: string[];
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const badgeRef = useRef<HTMLSpanElement>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const color = score >= 80 ? 'green' : score >= 60 ? 'yellow' : 'gray';
  const colors = {
    green: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300',
    gray: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  };

  const hasBreakdown = breakdown && (
    (breakdown.skills?.matches?.length || 0) > 0 ||
    (breakdown.certifications?.matches?.length || 0) > 0 ||
    (breakdown.experienceTitles?.matches?.length || 0) > 0 ||
    (breakdown.education?.matches?.length || 0) > 0
  );

  const handleMouseEnter = () => {
    if (badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      const tooltipHeight = 384; // max-h-96 = 24rem = 384px
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;

      if (spaceAbove > tooltipHeight || spaceAbove > spaceBelow) {
        // Show above
        setTooltipStyle({
          position: 'fixed',
          left: rect.left,
          bottom: window.innerHeight - rect.top + 8,
        });
      } else {
        // Show below
        setTooltipStyle({
          position: 'fixed',
          left: rect.left,
          top: rect.bottom + 8,
        });
      }
    }
    setShowTooltip(true);
  };

  return (
    <div className="relative inline-block">
      <span
        ref={badgeRef}
        className={`px-2.5 py-1 rounded-full text-sm font-medium cursor-help ${colors[color]}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {score}% match
        {hasBreakdown && <InfoIcon className="w-3 h-3 inline ml-1 opacity-60" />}
      </span>

      {/* Tooltip - fixed positioning to avoid clipping by any ancestor */}
      {showTooltip && (breakdown || vectorScore !== undefined || textScore !== undefined) && (
        <div style={tooltipStyle} className="z-50 w-80 max-h-96 overflow-y-auto bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-xl shadow-xl p-4 text-sm">
          {/* Score Summary */}
          {breakdown?.summary && (
            <p className="text-gray-700 dark:text-gray-300 mb-3 font-medium">
              {breakdown.summary}
            </p>
          )}

          {/* Score Components */}
          {(vectorScore !== undefined || textScore !== undefined) && (
            <div className="mb-3 pb-3 border-b dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider">Score Components</p>
              <div className="space-y-1">
                {vectorScore !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Semantic Match</span>
                    <span className="font-medium text-gray-900 dark:text-white">{vectorScore}%</span>
                  </div>
                )}
                {textScore !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Keyword Match</span>
                    <span className="font-medium text-gray-900 dark:text-white">{textScore}%</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Matched Terms */}
          {matchedTerms && matchedTerms.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Matched Terms</p>
              <div className="flex flex-wrap gap-1">
                {matchedTerms.map((term, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded text-xs"
                  >
                    {term}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Skills Matches */}
          {breakdown?.skills && breakdown.skills.matches.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider">
                Skill Matches
                <span className="ml-1 text-primary-600 dark:text-primary-400">
                  ({breakdown.skills.score}/{breakdown.skills.maxScore} pts)
                </span>
              </p>
              <div className="flex flex-wrap gap-1">
                {breakdown.skills.matches.map((match, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-xs"
                    title={`Matched "${match.term}" • ${match.years}y experience`}
                  >
                    {match.skill} ({match.years}y)
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Certifications Matches */}
          {breakdown?.certifications && breakdown.certifications.matches.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider">
                Certification Matches
                <span className="ml-1 text-amber-600 dark:text-amber-400">
                  ({breakdown.certifications.score}/{breakdown.certifications.maxScore} pts)
                </span>
              </p>
              <div className="flex flex-wrap gap-1">
                {breakdown.certifications.matches.map((match, i) => (
                  <span
                    key={i}
                    className={`px-2 py-0.5 rounded text-xs ${
                      match.isRecent
                        ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                    title={`Matched "${match.term}"${match.isRecent ? ' • Recent' : ''}`}
                  >
                    {match.name.length > 25 ? match.name.slice(0, 25) + '...' : match.name}
                    {match.isRecent && ' *'}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Experience Title Matches */}
          {breakdown?.experienceTitles && breakdown.experienceTitles.matches.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider">
                Role Matches
                <span className="ml-1 text-purple-600 dark:text-purple-400">
                  ({breakdown.experienceTitles.score}/{breakdown.experienceTitles.maxScore} pts)
                </span>
              </p>
              <div className="space-y-1">
                {breakdown.experienceTitles.matches.slice(0, 3).map((match, i) => (
                  <div
                    key={i}
                    className={`text-xs ${
                      match.isRecent
                        ? 'text-purple-700 dark:text-purple-300'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    <span className="font-medium">{match.title}</span>
                    <span className="text-gray-400 dark:text-gray-500"> at </span>
                    <span>{match.company}</span>
                    {match.isRecent && <span className="ml-1 text-purple-500">(current)</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Education Matches */}
          {breakdown?.education && breakdown.education.matches.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider">
                Education Matches
                <span className="ml-1 text-indigo-600 dark:text-indigo-400">
                  ({breakdown.education.score}/{breakdown.education.maxScore} pts)
                </span>
              </p>
              <div className="space-y-1">
                {breakdown.education.matches.slice(0, 2).map((match, i) => (
                  <div key={i} className="text-xs text-gray-600 dark:text-gray-400">
                    <span className="font-medium">{match.degree}</span>
                    <span className="text-gray-400 dark:text-gray-500"> from </span>
                    <span>{match.institution}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Experience Description Matches */}
          {breakdown?.experienceDescriptions && breakdown.experienceDescriptions.matchCount > 0 && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              +{breakdown.experienceDescriptions.matchCount} keyword matches in experience descriptions
              <span className="ml-1 text-gray-400 dark:text-gray-500">
                ({breakdown.experienceDescriptions.score}/{breakdown.experienceDescriptions.maxScore} pts)
              </span>
            </div>
          )}

          {/* No breakdown available */}
          {!hasBreakdown && !matchedTerms?.length && vectorScore === undefined && textScore === undefined && (
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              Score based on semantic similarity to your search query.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// Info Icon for score badge
function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

// Utility function
function getDateFromRange(range: string): Date {
  const now = new Date();
  switch (range) {
    case 'today':
      return new Date(now.setHours(0, 0, 0, 0));
    case 'week':
      return new Date(now.setDate(now.getDate() - 7));
    case 'month':
      return new Date(now.setMonth(now.getMonth() - 1));
    default:
      return new Date(0);
  }
}

// Icons
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function FilterIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function ViewIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function ExportIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}

function BriefcaseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function ListIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
    </svg>
  );
}

function GridIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  );
}

function ExpandIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function ChevronUpIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  );
}

function MailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function CertificateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  );
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}
